import { api, makeApiRequest } from './_init'
import { LOADING, UPDATE, FAIL, OVERWRITE, FAIL_EMPTY } from '../reducers/default'
import { returnErrorMessage } from 'utils/helpers'

export const fetchEventFbInsights = async (dispatch, eventId) => {
  dispatch({ type: LOADING })

  if (eventId) {
    try {
      const result = await api.get(`/integrations/facebook/events/${eventId}/fb_insights`)

      dispatch({ type: UPDATE, payload: result.data })
    } catch (error) {
      dispatch({ type: FAIL_EMPTY })
    }
  }
}

export const fetchCampaignResults = async (dispatch, eventId) => {
  dispatch({ type: LOADING })

  if (eventId) {
    try {
      const result = await api.get(`/integrations/facebook/events/${eventId}/campaign_results`)

      dispatch({ type: UPDATE, payload: result.data })
    } catch (error) {
      const code = error.response?.data?.error_code || error.error_code || null

      if (code) {
        dispatch({
          type: OVERWRITE,
          payload: {
            error_code: code,
            message: returnErrorMessage({ ...error, displayTextOnly: true }),
          },
        })
        dispatch({
          type: FAIL,
          payload: returnErrorMessage({ ...error, displayTextOnly: true }),
        })
      } else {
        dispatch({
          type: FAIL_EMPTY,
          payload: returnErrorMessage({ ...error, displayTextOnly: true }),
        })
      }
    }
  }
}

export const fetchAdPreviews = async (dispatch, eventId) => {
  dispatch({ type: LOADING })
  try {
    const result = await api.get(`/integrations/facebook/${eventId}/ads_previews`)
    let parsed = result.data.map((el, index) => {
      let template = document.createElement('template')
      el = el.trim()
      template.innerHTML = el
      let iframeEl = template.content.firstChild

      return {
        src: iframeEl.src,
        width: iframeEl.width,
        height: iframeEl.height,
        title: `meta-frame-${index}`,
      }
    })

    dispatch({ type: UPDATE, payload: parsed })
  } catch (error) {
    dispatch({ type: FAIL })
  }
}

export const fetchPageInfos = async (dispatch, fbPageId) => {
  dispatch({ type: LOADING })

  try {
    const result = await api.get(`/integrations/facebook/page_infos?fb_page_id=${fbPageId}`)
    dispatch({ type: UPDATE, payload: result.data })
    return result.data
  } catch (error) {
    dispatch({ type: FAIL })
    return {
      error: true,
    }
  }
}

export const fetchConversionGoals = async (dispatch, adAccountId, leadFormId = null) => {
  dispatch({ type: LOADING })

  try {
    const query = leadFormId ? `?lead_form_id=${leadFormId}` : ''
    const result = await api.get(
      `/integrations/connect/facebook/assets/ad_accounts/${adAccountId}/conversions${query}`,
    )
    dispatch({ type: OVERWRITE, payload: result.data?.items })
  } catch (error) {
    dispatch({ type: FAIL, payload: returnErrorMessage({ ...error, displayTextOnly: true }) })
  }
}

export const fetchLeadForms = async (dispatch, pageId, adAccountId) => {
  dispatch({ type: LOADING })

  try {
    const result = await api.get(
      `/integrations/connect/facebook/assets/lead_forms?page_id=${pageId}&ad_account_id=${adAccountId}`,
    )
    dispatch({ type: OVERWRITE, payload: result.data })
  } catch (error) {
    dispatch({ type: FAIL, payload: returnErrorMessage({ ...error, displayTextOnly: true }) })
  }
}

export const fetchCustomConversionById = async (dispatch, adAccountId, conversionId) => {
  dispatch({ type: LOADING })

  try {
    const _conversionId = conversionId.split('.').pop()
    const result = await api.get(
      `/integrations/connect/facebook/assets/ad_accounts/${adAccountId}/conversions/${_conversionId}`,
    )
    dispatch({ type: OVERWRITE, payload: result.data })
  } catch (error) {
    dispatch({ type: FAIL, payload: returnErrorMessage({ ...error, displayTextOnly: true }) })
  }
}

export const fetchAudiences = async (dispatch, adAccountId, type) => {
  dispatch({ type: LOADING })

  try {
    const result = await api.get(
      `/integrations/connect/facebook/assets/ad_accounts/${adAccountId}/audiences?type=${type}`,
    )
    dispatch({ type: OVERWRITE, payload: result.data?.items })
  } catch (error) {
    dispatch({ type: FAIL, payload: returnErrorMessage({ ...error, displayTextOnly: true }) })
  }
}

const integrationsApi = {
  /**
   * Fetches paginated insights breakdown for an event by mode (ads or tasteClusters).
   * @param {string} eventId - The event ID
   * @param {string} mode - 'ads' or 'tasteClusters'
   * @param {object} [options] - Pagination options: { page, limit }
   * @returns {Promise<object>} Paginated breakdown data
   */
  fetchInsightsBreakdown: async (eventId, mode = 'ads', options = {}) => {
    if (!eventId) throw new Error('eventId is required')
    const { page = 1, limit = 30, sortBy, sortOrder } = options

    const result = await api.get(`/integrations/facebook/events/${eventId}/fb_insights/insights-breakdown`, {
      params: {
        breakdown_by: mode,
        page,
        limit,
        sort_by: sortBy,
        sort_order: sortOrder,
      },
    })
    return result.data
  },

  /**
   * Fetches the top 5 performing ads for an event.
   * @param {string} eventId - The event ID
   * @returns {Promise<{ metadata: object, ads: Array<object> }>} The metadata and ads list
   */
  fetchTopPerformingAds: async (eventId) => {
    if (!eventId) throw new Error('eventId is required')
    const response = await makeApiRequest(api, 'get', `integrations/facebook/events/${eventId}/fb_insights/top-performing-ads`)
    return response
  },
}

export const fetchDsaRecommendations = async (adAccountId) => {
  try {
    const response = await makeApiRequest(api, 'get', `/integrations/connect/facebook/assets/ad_accounts/${adAccountId}/dsa_recommendations`)

    if (response.status === 200) {
      return {
        data: response.data,
        error: false,
        success: true,
      }
    }

    return {
      data: response.data,
      error: true,
      success: false,
    }
  } catch (error) {
    return {
      data: null,
      error: true,
      success: false,
    }
  }
}

export default integrationsApi
